/**
 * Pure helpers for hydrating the printer store: legacy-record normalisation,
 * the persisted printing method, and the stale-event rule. Kept free of the
 * store and of any driver so they are unit-testable in isolation.
 */
import type { SavedPrinter, PrinterMethod } from 'src/services/printers/printer-types';

/**
 * Records written before ESC/POS existed have no `driver`. They are Star, and
 * saying so explicitly is the whole migration: no field is renamed, removed or
 * reinterpreted, so running this on an already-normalised list is a no-op.
 * A record that cannot be read at all is dropped rather than left to crash a
 * print later; an ESC/POS record needs its endpoint to be usable.
 */
export function normalizePrinters(printers: readonly unknown[]): {
  printers: SavedPrinter[];
  changed: boolean;
} {
  let changed = false;
  const normalized: SavedPrinter[] = [];
  printers.forEach((raw) => {
    if (typeof raw !== 'object' || raw === null) {
      changed = true;
      return;
    }
    const record = raw as Partial<SavedPrinter> & { driver?: unknown };
    if (typeof record.identifier !== 'string' || typeof record.connection !== 'object' || record.connection === null) {
      changed = true;
      return;
    }
    if (record.driver === 'escpos') {
      const connection = record.connection as {
        transport?: unknown;
        ipAddress?: unknown;
        port?: unknown;
        queueName?: unknown;
        portName?: unknown;
      };
      const transport =
        (record as { transport?: unknown }).transport ?? connection.transport ?? 'lan';
      const usable =
        (transport === 'lan' && typeof connection.ipAddress === 'string' && typeof connection.port === 'number') ||
        (transport === 'windows-raw' && typeof connection.queueName === 'string') ||
        (transport === 'serial' && typeof connection.portName === 'string');
      if (!usable) {
        changed = true;
        return;
      }
      // Records saved before attached printers existed have no `transport`; they are LAN.
      if ((record as { transport?: unknown }).transport === undefined) changed = true;
      normalized.push({
        ...(record as SavedPrinter),
        transport,
        profile: (record as { profile?: object }).profile ?? {},
      } as SavedPrinter);
      return;
    }
    if (record.driver !== 'star') {
      changed = true;
      normalized.push({ ...(record as SavedPrinter), driver: 'star' } as SavedPrinter);
      return;
    }
    normalized.push(record as SavedPrinter);
  });
  return { printers: normalized, changed };
}

/** The saved method, else the active printer's driver, else Star (today's only method). */
export function resolvePrinterMethod(
  saved: PrinterMethod | undefined,
  active: SavedPrinter | null
): PrinterMethod {
  if (saved === 'star' || saved === 'escpos') return saved;
  if (active?.driver === 'escpos') return 'escpos';
  return 'star';
}

/**
 * Whether a status tagged `printerId` describes the active printer.
 *
 * - `null`: the driver could not name the printer (the mobile Star SDK). It
 *   is taken to mean the active printer, but only when that printer is Star.
 * - A LAN endpoint id (`lan:` / `escpos:`) names one printer exactly.
 * - Any other id is an opaque Star SDK handle: about the active printer when
 *   that printer is Star, otherwise stale.
 */
export function isStatusForActivePrinter(active: SavedPrinter | null, printerId: string | null): boolean {
  if (!active) return false;
  if (printerId === null) return active.driver !== 'escpos';
  if (printerId === active.identifier) return true;
  if (printerId.startsWith('lan:') || printerId.startsWith('escpos:')) return false;
  return active.driver !== 'escpos';
}
